/**
 * Trainer publishes focus / PiP policy; students subscribe for live updates.
 */

import { useEffect, useRef, useState } from "react";
import { useMaybeRoomContext } from "@livekit/components-react";
import { RoomEvent } from "livekit-client";
import {
  decodeLiveSessionPolicy,
  encodeLiveSessionPolicy,
  type LiveSessionPolicyMessage,
} from "./liveSessionPolicySync";

type PolicyFields = Omit<LiveSessionPolicyMessage, "type" | "v">;

type Args = {
  role: "trainer" | "student";
  enabled?: boolean;
  /** Trainer-only: fields to broadcast. */
  publish?: PolicyFields | null;
  /** Student seed from token / session_settings before first broadcast. */
  initial?: PolicyFields | null;
};

export function useLiveSessionPolicySync({
  role,
  enabled = true,
  publish = null,
  initial = null,
}: Args): LiveSessionPolicyMessage | null {
  const room = useMaybeRoomContext();
  const [remote, setRemote] = useState<LiveSessionPolicyMessage | null>(() =>
    initial
      ? {
          type: "codelive.session-policy",
          v: 1,
          ...initial,
        }
      : null,
  );
  const lastSentRef = useRef("");

  useEffect(() => {
    if (!initial || role !== "student") return;
    setRemote((prev) => {
      if (prev) return prev;
      return {
        type: "codelive.session-policy",
        v: 1,
        ...initial,
      };
    });
  }, [initial, role]);

  useEffect(() => {
    if (!enabled || role !== "student" || !room) return;

    const onData = (payload: Uint8Array) => {
      const msg = decodeLiveSessionPolicy(payload);
      if (msg) setRemote(msg);
    };

    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [enabled, role, room]);

  useEffect(() => {
    if (!enabled || role !== "trainer" || !room || !publish) return;

    const send = () => {
      if (!room.localParticipant) return;
      void room.localParticipant
        .publishData(encodeLiveSessionPolicy(publish), { reliable: true })
        .then(() => {
          lastSentRef.current = JSON.stringify(publish);
        })
        .catch(() => {
          /* ignore */
        });
    };

    const key = JSON.stringify(publish);
    if (key !== lastSentRef.current) {
      send();
    }

    // Policy rarely changes — heartbeat every 30s after immediate publish.
    const id = window.setInterval(send, 30000);
    return () => window.clearInterval(id);
  }, [enabled, role, room, publish]);

  return role === "student" ? remote : null;
}
